import * as React from 'react';
import { KeyboardArrowLeft } from '@material-ui/icons';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';

export interface CollapsibleSidebarProps {
  /** Collapse state on first render */
  initialCollapse: boolean;
  /** current collapse state */
  collapsed: boolean;
  /** function to set collapse state */
  setCollapsed: (collapsed: boolean) => void;
  /** child component: a menu */
  children: React.ReactNode;
  /** class with extra styles */
  className?: string;
  /** width when open */
  width: string;
  /** collapsed width */
  collapsedWidth: string;
}

interface StyleProps {
  width: string;
  collapsedWidth: string;
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  root: (props) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    position: 'relative',
    width: props.width,
    transition: 'width 200ms',
    '&.collapsed': {
      width: props.collapsedWidth
    }
  }),
  collapseButton: {
    position: 'absolute',
    top: '2px',
    right: '-13px',
    borderRadius: '50%',
    backgroundColor: theme.palette.background.default,
    width: '26px',
    height: '26px',
    color: '#000',
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main
    }
  },
  icon: {
    fontSize: '29px',
    color: theme.palette.secondary.main,
    transform: 'rotate(0)',
    transition: 'transform 300ms',
    '&.collapsed': {
      transform: 'rotate(-180deg)'
    },
    '&:hover': {
      color: theme.palette.common.white
    }
  },
  childrenContainer: {
    overflow: 'hidden'
  }
}));

/**
 * Higher order component which renders a collapsible sidebar area for a child menu
 */
const CollapsibleSidebar = ({
  initialCollapse,
  collapsed,
  setCollapsed,
  children,
  className,
  width,
  collapsedWidth
}: CollapsibleSidebarProps) => {
  const classes = useStyles({ width, collapsedWidth });

  React.useEffect(() => {
    if (initialCollapse != collapsed) {
      setCollapsed(initialCollapse);
    }
  }, []);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div className={clsx(classes.root, className, collapsed && 'collapsed')}>
      {children}
      <div className={classes.collapseButton} onClick={toggleCollapse}>
        <KeyboardArrowLeft className={clsx(classes.icon, collapsed && 'collapsed')} />
      </div>
    </div>
  );
};

export default CollapsibleSidebar;
